import { AccountGroupRole, CIBPageName } from '@shared/enums';
import { ApiUser } from '@api/interfaces';
import { getTranslateFunction } from '@/i18n';

interface BannerContent {
  messages: string[],
  title: string,
  mixedGroupContent?: {
    title: string,
    messages: string[],
  }
}

interface PageBannerContent {
  [key: string]: BannerContent | undefined,
}

const getBannerContentMap = (language: string): Partial<Record<CIBPageName, PageBannerContent>> => {
  const t = getTranslateFunction(language);
  return {
    [CIBPageName.CRD_BookingPage]: {
      [AccountGroupRole.Primary]: {
        title: '',
        messages: [
          t(($) => (
            $.customerDashboard.linkedAccounts.contextualBanner
              .crdBookingPage.primary.message
          )),
        ],
        mixedGroupContent: {
          title: '',
          messages: [
            t(($) => (
              $.customerDashboard.linkedAccounts.contextualBanner
                .crdBookingPage.primaryMixed.message
            )),
          ],
        },
      },
      [AccountGroupRole.Partner]: {
        title: '',
        messages: [
          t(($) => (
            $.customerDashboard.linkedAccounts.contextualBanner
              .crdBookingPage.partner.message
          )),
        ],
        mixedGroupContent: {
          title: '',
          messages: [
            t(($) => (
              $.customerDashboard.linkedAccounts.contextualBanner
                .crdBookingPage.partnerMixed.message
            )),
          ],
        },
      },
      [AccountGroupRole.IndependentPartner]: {
        title: '',
        messages: [
          t(($) => (
            $.customerDashboard.linkedAccounts.contextualBanner
              .crdBookingPage.independentPartner.message
          )),
        ],
        mixedGroupContent: {
          title: '',
          messages: [
            t(($) => (
              $.customerDashboard.linkedAccounts.contextualBanner
                .crdBookingPage.independentPartnerMixed.message
            )),
          ],
        },
      },
    },
    [CIBPageName.RP_CurrentProgress]: {
      [AccountGroupRole.Primary]: {
        title: '',
        messages: [
          t(($) => (
            $.customerDashboard.linkedAccounts.contextualBanner
              .rpCurrentProgress.primary.message
          )),
        ],
      },
      [AccountGroupRole.Secondary]: {
        title: '',
        messages: [
          t(($) => (
            $.customerDashboard.linkedAccounts.contextualBanner
              .rpCurrentProgress.secondary.message
          )),
        ],
      },
      [AccountGroupRole.Partner]: {
        title: '',
        messages: [
          t(($) => (
            $.customerDashboard.linkedAccounts.contextualBanner
              .rpCurrentProgress.partner.message
          )),
        ],
      },
      [AccountGroupRole.IndependentPartner]: {
        title: '',
        messages: [
          t(($) => (
            $.customerDashboard.linkedAccounts.contextualBanner
              .rpCurrentProgress.independentPartner.message
          )),
        ],
      },
      'mix-role': {
        title: '',
        messages: [
          t(($) => (
            $.customerDashboard.linkedAccounts.contextualBanner
              .rpCurrentProgress.mixRole.message
          )),
        ],
      },
    },
    [CIBPageName.RP_CustomerRecord]: {
      [AccountGroupRole.Primary]: {
        title: t(($) => (
          $.customerDashboard.linkedAccounts.contextualBanner
            .rpCustomerRecord.primary.title
        )),
        messages: [
          t(($) => (
            $.customerDashboard.linkedAccounts.contextualBanner
              .rpCustomerRecord.primary.message
          )),
        ],
      },
      [AccountGroupRole.Secondary]: {
        title: t(($) => (
          $.customerDashboard.linkedAccounts.contextualBanner
            .rpCustomerRecord.secondary.title
        )),
        messages: [
          t(($) => (
            $.customerDashboard.linkedAccounts.contextualBanner
              .rpCustomerRecord.secondary.message
          )),
        ],
      },
      [AccountGroupRole.Partner]: {
        title: t(($) => (
          $.customerDashboard.linkedAccounts.contextualBanner
            .rpCustomerRecord.partner.title
        )),
        messages: [
          t(($) => (
            $.customerDashboard.linkedAccounts.contextualBanner
              .rpCustomerRecord.partner.message
          )),
        ],
      },
      [AccountGroupRole.IndependentPartner]: {
        title: t(($) => (
          $.customerDashboard.linkedAccounts.contextualBanner
            .rpCustomerRecord.independentPartner.title
        )),
        messages: [
          t(($) => (
            $.customerDashboard.linkedAccounts.contextualBanner
              .rpCustomerRecord.independentPartner.message
          )),
        ],
      },
      'mix-role': {
        title: t(($) => (
          $.customerDashboard.linkedAccounts.contextualBanner
            .rpCustomerRecord.mixRole.title
        )),
        messages: [
          t(($) => (
            $.customerDashboard.linkedAccounts.contextualBanner
              .rpCustomerRecord.mixRole.message
          )),
        ],
      },
    },
    [CIBPageName.RP_PointsActivity]: {
      [AccountGroupRole.Primary]: {
        title: '',
        messages: [
          t(($) => (
            $.customerDashboard.linkedAccounts.contextualBanner
              .rpPointsActivity.primary.message
          )),
        ],
      },
      [AccountGroupRole.Secondary]: {
        title: '',
        messages: [
          t(($) => (
            $.customerDashboard.linkedAccounts.contextualBanner
              .rpPointsActivity.secondary.message
          )),
        ],
      },
      [AccountGroupRole.Partner]: {
        title: '',
        messages: [
          t(($) => (
            $.customerDashboard.linkedAccounts.contextualBanner
              .rpPointsActivity.partner.message
          )),
        ],
      },
      [AccountGroupRole.IndependentPartner]: {
        title: '',
        messages: [
          t(($) => (
            $.customerDashboard.linkedAccounts.contextualBanner
              .rpPointsActivity.independentPartner.message
          )),
        ],
      },
      'mix-role': {
        title: '',
        messages: [
          t(($) => (
            $.customerDashboard.linkedAccounts.contextualBanner
              .rpPointsActivity.mixRole.message
          )),
        ],
      },
    },
    [CIBPageName.RP_Wallet]: {
      [AccountGroupRole.Primary]: {
        title: '',
        messages: [
          t(($) => (
            $.customerDashboard.linkedAccounts.contextualBanner
              .rpWallet.primary.message
          )),
        ],
      },
      [AccountGroupRole.Secondary]: {
        title: '',
        messages: [
          t(($) => (
            $.customerDashboard.linkedAccounts.contextualBanner
              .rpWallet.secondary.message
          )),
        ],
      },
      [AccountGroupRole.Partner]: {
        title: '',
        messages: [
          t(($) => (
            $.customerDashboard.linkedAccounts.contextualBanner
              .rpWallet.partner.message
          )),
        ],
      },
      [AccountGroupRole.IndependentPartner]: {
        title: '',
        messages: [
          t(($) => (
            $.customerDashboard.linkedAccounts.contextualBanner
              .rpWallet.independentPartner.message
          )),
        ],
      },
      'mix-role': {
        title: '',
        messages: [
          t(($) => (
            $.customerDashboard.linkedAccounts.contextualBanner
              .rpWallet.mixRole.message
          )),
        ],
      },
    },
    [CIBPageName.CRD_PointsActivity]: {
      [AccountGroupRole.Primary]: {
        title: '',
        messages: [
          t(($) => (
            $.customerDashboard.linkedAccounts.contextualBanner
              .crdPointsActivity.primary.message
          )),
        ],
        mixedGroupContent: {
          title: '',
          messages: [
            t(($) => (
              $.customerDashboard.linkedAccounts.contextualBanner
                .crdPointsActivity.primaryMixed.message
            )),
          ],
        },
      },
      [AccountGroupRole.Partner]: {
        title: '',
        messages: [
          t(($) => (
            $.customerDashboard.linkedAccounts.contextualBanner
              .crdPointsActivity.partner.message
          )),
        ],
        mixedGroupContent: {
          title: '',
          messages: [
            t(($) => (
              $.customerDashboard.linkedAccounts.contextualBanner
                .crdPointsActivity.partnerMixed.message
            )),
          ],
        },
      },
      [AccountGroupRole.IndependentPartner]: {
        title: '',
        messages: [
          t(($) => (
            $.customerDashboard.linkedAccounts.contextualBanner
              .crdPointsActivity.independentPartner.message
          )),
        ],
        mixedGroupContent: {
          title: '',
          messages: [
            t(($) => (
              $.customerDashboard.linkedAccounts.contextualBanner
                .crdPointsActivity.independentPartnerMixed.message
            )),
          ],
        },
      },
    },
    [CIBPageName.CRD_CurrentProgress_TierProgress]: {
      [AccountGroupRole.Primary]: {
        title: '',
        messages: [
          t(($) => (
            $.customerDashboard.linkedAccounts.contextualBanner
              .crdTierProgress.primary.message
          )),
          t(($) => (
            $.customerDashboard.linkedAccounts.contextualBanner
              .crdTierProgress.primary.message2
          )),
        ],
      },
      [AccountGroupRole.Partner]: {
        title: '',
        messages: [
          t(($) => (
            $.customerDashboard.linkedAccounts.contextualBanner
              .crdTierProgress.partner.message
          )),
        ],
        mixedGroupContent: {
          title: '',
          messages: [
            t(($) => (
              $.customerDashboard.linkedAccounts.contextualBanner
                .crdTierProgress.partnerMixed.message
            )),
            t(($) => (
              $.customerDashboard.linkedAccounts.contextualBanner
                .crdTierProgress.partnerMixed.message2
            )),
          ],
        },
      },
      [AccountGroupRole.IndependentPartner]: {
        title: '',
        messages: [
          t(($) => (
            $.customerDashboard.linkedAccounts.contextualBanner
              .crdTierProgress.independentPartner.message
          )),
        ],
        mixedGroupContent: {
          title: '',
          messages: [
            t(($) => (
              $.customerDashboard.linkedAccounts.contextualBanner
                .crdTierProgress.independentPartnerMixed.message
            )),
          ],
        },
      },
    },
    [CIBPageName.CRD_CurrentProgress_SpendingBreakdown]: {
      [AccountGroupRole.Primary]: {
        title: '',
        messages: [
          t(($) => (
            $.customerDashboard.linkedAccounts.contextualBanner
              .crdSpendingBreakdown.primary.message
          )),
          t(($) => (
            $.customerDashboard.linkedAccounts.contextualBanner
              .crdSpendingBreakdown.primary.message2
          )),
        ],
      },
      [AccountGroupRole.Partner]: {
        title: '',
        messages: [
          t(($) => (
            $.customerDashboard.linkedAccounts.contextualBanner
              .crdSpendingBreakdown.partner.message
          )),
          t(($) => (
            $.customerDashboard.linkedAccounts.contextualBanner
              .crdSpendingBreakdown.partner.message2
          )),
        ],
      },
      [AccountGroupRole.IndependentPartner]: {
        title: '',
        messages: [
          t(($) => (
            $.customerDashboard.linkedAccounts.contextualBanner
              .crdSpendingBreakdown.independentPartner.message
          )),
        ],
        mixedGroupContent: {
          title: '',
          messages: [
            t(($) => (
              $.customerDashboard.linkedAccounts.contextualBanner
                .crdSpendingBreakdown.independentPartnerMixed.message
            )),
          ],
        },
      },
    },
    [CIBPageName.CRD_LandingPage]: {
      [AccountGroupRole.Primary]: {
        title: t(($) => (
          $.customerDashboard.linkedAccounts.contextualBanner
            .crdLandingPage.primary.title
        )),
        messages: [
          t(($) => (
            $.customerDashboard.linkedAccounts.contextualBanner
              .crdLandingPage.primary.message
          )),
        ],
        mixedGroupContent: {
          title: t(($) => (
            $.customerDashboard.linkedAccounts.contextualBanner
              .crdLandingPage.primaryMixed.title
          )),
          messages: [
            t(($) => (
              $.customerDashboard.linkedAccounts.contextualBanner
                .crdLandingPage.primaryMixed.message
            )),
          ],
        },
      },
      [AccountGroupRole.Partner]: {
        title: t(($) => (
          $.customerDashboard.linkedAccounts.contextualBanner
            .crdLandingPage.partner.title
        )),
        messages: [
          t(($) => (
            $.customerDashboard.linkedAccounts.contextualBanner
              .crdLandingPage.partner.message
          )),
        ],
        mixedGroupContent: {
          title: t(($) => (
            $.customerDashboard.linkedAccounts.contextualBanner
              .crdLandingPage.partnerMixed.title
          )),
          messages: [
            t(($) => (
              $.customerDashboard.linkedAccounts.contextualBanner
                .crdLandingPage.partnerMixed.message
            )),
          ],
        },
      },
      [AccountGroupRole.IndependentPartner]: {
        title: t(($) => (
          $.customerDashboard.linkedAccounts.contextualBanner
            .crdLandingPage.independentPartner.title
        )),
        messages: [
          t(($) => (
            $.customerDashboard.linkedAccounts.contextualBanner
              .crdLandingPage.independentPartner.message
          )),
        ],
        mixedGroupContent: {
          title: t(($) => (
            $.customerDashboard.linkedAccounts.contextualBanner
              .crdLandingPage.independentPartnerMixed.title
          )),
          messages: [
            t(($) => (
              $.customerDashboard.linkedAccounts.contextualBanner
                .crdLandingPage.independentPartnerMixed.message
            )),
          ],
        },
      },
    },
    [CIBPageName.CRD_Wallet]: {
      [AccountGroupRole.Primary]: {
        title: '',
        messages: [
          t(($) => (
            $.customerDashboard.linkedAccounts.contextualBanner
              .crdWallet.primary.message
          )),
          t(($) => (
            $.customerDashboard.linkedAccounts.contextualBanner
              .crdWallet.primary.message2
          )),
        ],
        mixedGroupContent: {
          title: '',
          messages: [
            t(($) => (
              $.customerDashboard.linkedAccounts.contextualBanner
                .crdWallet.primaryMixed.message
            )),
          ],
        },
      },
      [AccountGroupRole.Partner]: {
        title: '',
        messages: [
          t(($) => (
            $.customerDashboard.linkedAccounts.contextualBanner
              .crdWallet.partner.message
          )),
        ],
        mixedGroupContent: {
          title: '',
          messages: [
            t(($) => (
              $.customerDashboard.linkedAccounts.contextualBanner
                .crdWallet.partnerMixed.message
            )),
          ],
        },
      },
      [AccountGroupRole.IndependentPartner]: {
        title: '',
        messages: [
          t(($) => (
            $.customerDashboard.linkedAccounts.contextualBanner
              .crdWallet.independentPartner.message
          )),
        ],
      },
    },
  };
};

export const getBannerContent = (
  customerRole: AccountGroupRole,
  linkedAccounts: ApiUser[],
  page: CIBPageName,
  language: string,
): BannerContent | null => {
  const pageContent = getBannerContentMap(language)[page];

  if (!pageContent || !linkedAccounts.length) {
    return null;
  }

  const linkedAccountRoles = new Set<AccountGroupRole>([customerRole]);
  linkedAccounts.forEach((linked) => {
    if (linked.accountGroupRole) {
      linkedAccountRoles.add(linked.accountGroupRole);
    }
  });

  const hasPartner = linkedAccountRoles.has(AccountGroupRole.Partner);
  const hasIndependentPartner = linkedAccountRoles.has(AccountGroupRole.IndependentPartner);
  const hasPrimaryOrSecondary = linkedAccountRoles.has(AccountGroupRole.Primary)
    && linkedAccountRoles.has(AccountGroupRole.Secondary);

  const hasMixedRoles = [hasPrimaryOrSecondary, hasPartner, hasIndependentPartner]
    .filter(Boolean).length >= 2;

  if (hasMixedRoles) {
    if (customerRole && pageContent[customerRole]) {
      const mixedGroupContent = pageContent[customerRole]?.mixedGroupContent;
      if (mixedGroupContent) {
        return mixedGroupContent;
      }
    }
    const mixedRoleContent = pageContent['mix-role'];
    if (mixedRoleContent) {
      return mixedRoleContent;
    }
  }

  if (pageContent[customerRole]) {
    return pageContent[customerRole] as BannerContent;
  }

  return null;
};

export { CIBPageName };
export { CIBPageName as PageName };
